/**
 * How much of one convex footprint lies inside another.
 *
 * `doPolygonsOverlap` (SAT) answers yes/no and `overlapAmount` answers "how
 * deep", but neither answers "how MUCH of this object is covered" — the
 * question you need to tell a duplicate capture from two real objects that
 * merely touch. Two stair flights meeting at a landing overlap, and so do
 * two captures of the same flight; only the covered FRACTION separates them.
 *
 * Exact for convex polygons (Sutherland–Hodgman clip + shoelace), which is
 * all we ever pass it — `calculateCorners` returns rotated rectangles.
 *
 * NOTE the shoelace formula is currently open-coded in several places
 * (`reachableRegions.js`, `8-scoring.js`, `6-expansion.js`,
 * `fixtureRecommendation.js`). `polygonArea` here is meant to be the one
 * they migrate onto; it is exported for that reason.
 */

/** Absolute area of a simple polygon (shoelace). Winding-agnostic. */
function polygonArea(poly) {
  if (!poly || poly.length < 3) return 0;
  let sum = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    sum += poly[j].x * poly[i].z - poly[i].x * poly[j].z;
  }
  return Math.abs(sum) / 2;
}

/** Signed side of directed edge a->b that `p` falls on (>0 = left). */
function side(a, b, p) {
  return (b.x - a.x) * (p.z - a.z) - (b.z - a.z) * (p.x - a.x);
}

/** Intersection point of segment p->q with the infinite line a->b. */
function lineIntersect(p, q, a, b) {
  const d1 = side(a, b, p);
  const d2 = side(a, b, q);
  const denom = d1 - d2;
  if (Math.abs(denom) < 1e-12) return q;
  const t = d1 / denom;
  return { x: p.x + t * (q.x - p.x), z: p.z + t * (q.z - p.z) };
}

/**
 * Sutherland–Hodgman: clip convex `subject` against convex `clip`.
 * Returns the intersection polygon (possibly empty).
 */
function convexIntersection(subject, clip) {
  if (!subject?.length || !clip?.length) return [];
  // Normalise winding so "inside" is a consistent sign.
  const ccw = (poly) => {
    let sum = 0;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      sum += poly[j].x * poly[i].z - poly[i].x * poly[j].z;
    }
    return sum > 0 ? poly : [...poly].reverse();
  };
  let output = ccw(subject);
  const c = ccw(clip);

  for (let i = 0, j = c.length - 1; i < c.length; j = i++) {
    const a = c[j];
    const b = c[i];
    const input = output;
    output = [];
    for (let k = 0, m = input.length - 1; k < input.length; m = k++) {
      const prev = input[m];
      const cur = input[k];
      const prevIn = side(a, b, prev) >= 0;
      const curIn = side(a, b, cur) >= 0;
      if (curIn) {
        if (!prevIn) output.push(lineIntersect(prev, cur, a, b));
        output.push(cur);
      } else if (prevIn) {
        output.push(lineIntersect(prev, cur, a, b));
      }
    }
    if (!output.length) return [];
  }
  return output;
}

/** Area shared by two convex footprints. */
function convexOverlapArea(polyA, polyB) {
  return polygonArea(convexIntersection(polyA, polyB));
}

/**
 * Fraction of `inner`'s own area that lies inside `outer` — 1 when fully
 * contained, 0 when disjoint. Returns 0 for a degenerate `inner` rather
 * than dividing by zero.
 */
function containedFraction(inner, outer) {
  const a = polygonArea(inner);
  if (a <= 1e-12) return 0;
  return convexOverlapArea(inner, outer) / a;
}

module.exports = {
  polygonArea,
  convexIntersection,
  convexOverlapArea,
  containedFraction,
};
